import { action, observable } from "mobx";
import yup from "yup";
import { DDocumentFragment, EmailEditor_UploadAttachment } from "@graphql";
import { FormStore } from "@modules/formx/FormStore";
import locale from "@bonliva-crm/locale";
import { store } from "@store";

export type Attachment = DDocumentFragment;

export type EmailFormData = {
  subject: string;
  body: string;
  to: EmailRecipient[];
  attachments: Attachment[];
  suggestions?: LabelValue[];
  from?: string;
};

export type EmailRecipient = {
  email: string;
  name: string;
  avatarPic: string;
};

const schema = yup.object<EmailFormData>({
  subject: yup.string().required(locale.SelectSomething(locale.Subject)),
  body: yup.string().required(),
  to: yup
    .array(
      yup.object<EmailRecipient>({
        email: yup.string().email().required(),
        name: yup.string().nullable(true),
        avatarPic: yup.string().nullable(true)
      })
    )
    .min(1),
  attachments: yup.array(),
  suggestions: yup.array(),
  from: yup.string().nullable(true)
});

const initialValues: EmailFormData = {
  subject: "",
  body: "",
  to: [],
  attachments: [],
  suggestions: [],
  from: null
};

export class EmailEditorFormStore extends FormStore<EmailFormData> {
  @observable isAttachingFile = false;
  @observable showAddRecipient = false;
  @observable allowAttachments = false;

  constructor(_initialValues?: EmailFormData) {
    super({
      debounceValidationMs: 200,
      schema
    });

    this.reset(_initialValues || initialValues, true);

    (window as any).emailEditorFormStore = this;
  }

  @action.bound addRecipient(name: string, email: string, avatarPic = "") {
    if (this.values.to.find((x) => x.email === email)) {
      return;
    }

    this.values.to.push({ avatarPic, email, name });
    this.validate();
  }

  @action.bound removeRecipient(recipient: EmailRecipient) {
    this.values.to = this.values.to.filter(
      (x) => !(x.name === recipient.name && x.email === recipient.email)
    );

    this.validate();
  }

  @action removeAttachment = (item: Attachment) => {
    const attachments = this.values.attachments.removeElementPure(
      (x) => x.FileRef.NodeID === item.FileRef.NodeID
    );

    this.setValue("attachments", attachments);
  };

  @action selectFiles = () => {
    store()
      .upload.requestFileUpload({ maxSize: 4_000_000, multiple: true })
      .then(this.attachFiles);
  };

  @action attachFiles = (files: File[]) => {
    this.isAttachingFile = true;

    for (const file of files) {
      EmailEditor_UploadAttachment.func({ file })
        .then(({ data }) => {
          this.push("attachments", data.uploadDocument);
        })
        .catch((e) => {
          store().snackbars.error(`Cannot attach a file\nReason: ${e}`);
        });
    }

    this.isAttachingFile = false;
  };
}
